import React, { useMemo } from "react";
import { Crown, Medal, Star, Trophy } from "lucide-react";
import { iconOf } from "../lib/icons";
import { fileUrl } from "../lib/api";
import { motion } from "framer-motion";

const RANKS = [
  { icon: Crown, label: "الأول", ring: "border-[#F0E8DB]", badge: "from-[#F0E8DB] to-[#C9B9A0]" },
  { icon: Medal, label: "الثاني", ring: "border-[#F0E8DB]/60", badge: "from-[#E0D6C8] to-[#A89A88]" },
  { icon: Star, label: "الثالث", ring: "border-[#B87333]", badge: "from-[#D89A5E] to-[#8A5A2B]" },
];

function Person({ m, i }) {
  const r = RANKS[i];
  const Icon = r.icon;
  return (
    <div data-testid={m ? `mom-card-${m.id}` : `mom-placeholder-${i}`} className="flex flex-col items-center text-center flex-1 min-w-0">
      <div className="relative mb-2">
        <div className={`${i === 0 ? "w-20 h-20" : "w-16 h-16"} rounded-full border-2 ${r.ring} bg-[#241615] flex items-center justify-center overflow-hidden`}>
          {m?.image_url ? <img src={fileUrl(m.image_url)} alt={m.member_name} className="w-full h-full object-cover" />
            : <span className="text-xl font-bold text-[#F0E8DB]">{m ? (m.member_name || "؟")[0] : "؟"}</span>}
        </div>
        <div className={`absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-gradient-to-br ${r.badge} flex items-center justify-center text-[#1A0F0E]`}><Icon size={13} /></div>
      </div>
      <div className="text-[#F0E8DB]/60 text-[11px]">{r.label}</div>
      <div className="text-[#F0E8DB] text-sm font-semibold leading-tight">{m ? m.member_name : "قريباً"}</div>
      {m?.reason && <p className="text-[#C5B8A8] text-xs mt-1 leading-relaxed line-clamp-2">{m.reason}</p>}
    </div>
  );
}

function CommitteeBlock({ title, people, idx, isBest, iconKey }) {
  const Icon = iconOf(iconKey);
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: idx * 0.07 }}
      data-testid={`mom-committee-${title}`}
      className={`theater-card rounded-2xl p-5 ${isBest ? "border-[#F0E8DB]/70 gold-glow" : ""}`}
    >
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#F0E8DB]/15">
        <div className="w-9 h-9 rounded-lg bg-[#7A2E2A]/40 border border-[#F0E8DB]/40 flex items-center justify-center shrink-0"><Icon size={16} className="text-[#F0E8DB]" /></div>
        <h3 className="text-[#F0E8DB] font-semibold text-sm sm:text-base leading-tight">{title}</h3>
        {isBest && <span className="ms-auto text-[10px] px-2 py-0.5 rounded-full bg-[#F0E8DB] text-[#1A0F0E] font-bold whitespace-nowrap">أفضل لجنة</span>}
      </div>
      <div className="flex gap-2 items-start">
        {[1, 0, 2].map((i) => <Person key={i} m={people[i]} i={i} />)}
      </div>
    </motion.div>
  );
}

export default function MemberOfMonth({ items, settings, content }) {
  const grouped = useMemo(() => {
    const map = {};
    (items || []).forEach((m) => { (map[m.committee] ||= []).push(m); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (b.month || "").localeCompare(a.month || "") || (a.rank ?? 1) - (b.rank ?? 1)));
    return content.committee_list.map(({ name: c, icon }) => {
      const arr = map[c] || [];
      const latest = arr[0]?.month;
      const cur = arr.filter((m) => m.month === latest).sort((a, b) => (a.rank ?? 1) - (b.rank ?? 1));
      return [c, [0, 1, 2].map((r) => cur.find((m) => (m.rank ?? 1) === r + 1) || cur[r]), icon];
    });
  }, [items, content]);
  const best = settings?.best_committee;

  return (
    <section id="member-of-month" data-testid="mom-section" className="py-20 md:py-28 bg-[#120908] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="text-center mb-12">
          <div className="text-[#F0E8DB] text-xs tracking-[0.4em] font-serif-en mb-3">STARS OF THE MONTH</div>
          <h2 className="section-title text-3xl sm:text-4xl lg:text-5xl">{content.titles.mom}</h2>
          {content.mom_intro && <p className="text-[#A89A88] mt-4 max-w-xl mx-auto">{content.mom_intro}</p>}
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          data-testid="best-committee-card"
          className="relative rounded-3xl border border-[#F0E8DB]/60 bg-[#1f1110] overflow-hidden mb-8 p-8 sm:p-10 text-center"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(240,232,219,0.18),transparent_55%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F0E8DB]/50 bg-[#7A2E2A]/30 text-[#F0E8DB] text-xs tracking-widest mb-4"><Trophy size={14} /> أفضل لجنة هذا الشهر</div>
            <h3 className="text-[#F0E8DB] font-bold text-2xl sm:text-3xl">{best || "قريباً"}</h3>
            <p className="text-[#C5B8A8] mt-3 max-w-2xl mx-auto leading-relaxed">{best ? settings?.best_committee_reason : "تُعلن اللجنة المتميزة مع أول تكريم شهري."}</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {grouped.map(([c, people, icon], i) => <CommitteeBlock key={c} title={c} people={people} idx={i} isBest={best === c} iconKey={icon} />)}
        </div>
      </div>
    </section>
  );
}
